import { useCallback } from "react";
import { useAlertDialog } from "./useAlertDialog.tsx";
import type { GridEditModalStatus } from "../components/editSearch/gridEditModal.tsx";

/**
 * Inputs for the unsaved-changes guard.
 *
 * The hook owns two concerns that always travel together for edit-style modals:
 *   1. **Discard confirmation** — if the user tries to close while `dirty`, prompt
 *      via the shared `useAlertDialog` confirm flow before actually invoking
 *      `onClose`. Stale state skips the prompt (it's the server that diverged,
 *      not the user's local edits).
 *   2. **Inline status banner** — surface "N unsaved tabs" / "M stale sections"
 *      in a `GridEditModalStatus` shape that the canonical modal chrome
 *      (`GridEditModal.statusMessage`) accepts directly. Callers using raw
 *      `Modal` primitives render the same shape with their own banner.
 *
 * Stale takes precedence over dirty for banner severity because the user can't
 * resolve dirty until they've reloaded the stale section first.
 */
export interface UnsavedGuardOptions {
  /** True when there are local unsaved edits. */
  dirty: boolean;
  /** Inline text for the dirty banner (e.g. "3 unsaved tabs"). */
  dirtySummary?: string;
  /** True when remote state has moved underneath the user. */
  stale?: boolean;
  /** Inline text for the stale banner (e.g. "2 sections stale — reload first"). */
  staleSummary?: string;
  /** Override the discard-confirm title. */
  confirmTitle?: string;
  /** Override the discard-confirm body. */
  confirmBody?: string;
  /** Override the discard button label. */
  confirmLabel?: string;
  /** Underlying close handler. The guard only invokes this once the user confirms. */
  onClose: () => void;
}

export interface UnsavedGuard {
  /**
   * Status payload in `GridEditModalStatus` shape. Pipe directly into
   * `<GridEditModal statusMessage={guard.status} />` or render with any banner
   * component matching `{ type, message }`.
   */
  status: GridEditModalStatus | null;
  /**
   * Wrapped close handler. Pass this everywhere the modal closes (X button,
   * Cancel button, backdrop, Esc) — it prompts the user when needed and falls
   * through to `onClose` otherwise.
   */
  attemptClose: () => Promise<void>;
}

/**
 * Discard-confirm + inline-banner glue for edit-style modals. Co-locates the
 * "user is editing, mind the unsaved changes" logic so each modal doesn't
 * re-implement it.
 *
 * Pattern (PageEditorModal-style, raw Modal primitives):
 *
 * ```tsx
 * const guard = useUnsavedGuard({
 *   dirty: dirtyTabs.size > 0,
 *   dirtySummary: `${dirtyTabs.size} unsaved tab${dirtyTabs.size === 1 ? "" : "s"}`,
 *   stale: staleSections.size > 0,
 *   staleSummary: `${staleSections.size} stale — reload before saving`,
 *   onClose,
 * });
 *
 * <Modal open onClose={guard.attemptClose}>
 *   <ModalHeader title="…" onClose={guard.attemptClose} />
 *   <ModalBody>…</ModalBody>
 *   {guard.status && <StatusBanner status={guard.status} />}
 *   <ModalFooter>
 *     <Button onClick={guard.attemptClose}>Cancel</Button>
 *     <Button onClick={handleSave}>Save</Button>
 *   </ModalFooter>
 * </Modal>
 * ```
 *
 * Pattern (GridEditModal): pass `unsaved` / `unsavedSummary` directly — the
 * modal builds the guard internally and forwards `attemptClose` to all close
 * affordances.
 */
export function useUnsavedGuard({
  dirty,
  dirtySummary,
  stale,
  staleSummary,
  confirmTitle = "Discard unsaved changes?",
  confirmBody = "Any local edits you haven't saved will be lost.",
  confirmLabel = "Discard",
  onClose,
}: UnsavedGuardOptions): UnsavedGuard {
  const { confirm } = useAlertDialog();

  const status: GridEditModalStatus | null = stale
    ? { type: "warning", message: staleSummary ?? "Some sections were changed elsewhere" }
    : dirty
      ? { type: "info", message: dirtySummary ?? "Unsaved changes" }
      : null;

  const attemptClose = useCallback(async () => {
    // Stale always returns to onClose; the user has no local edits to lose at
    // that point — the server has the truth, the dirty banner gets nuked when
    // the modal reopens.
    if (dirty && !stale) {
      const ok = await confirm({
        title: confirmTitle,
        body: confirmBody,
        confirmLabel,
        destructive: true,
      });
      if (!ok) return;
    }
    onClose();
  }, [dirty, stale, confirm, confirmTitle, confirmBody, confirmLabel, onClose]);

  return { status, attemptClose };
}
